import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAnnouncements, Announcement } from '../context/AnnouncementContext';
import { firebaseEnabled, auth } from '../config/firebase';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLock, faEye, faEyeSlash, faPlus, faEdit, faTrash,
  faSignOutAlt, faBullhorn, faExclamationTriangle, faExclamationCircle,
  faTimes, faArrowLeft, faToggleOn, faToggleOff, faClock, faCheck, faBan,
  faEnvelope, faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import logo from '../media/logo_dentiste.png';

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'dentiste2024';

const typeConfig = {
  info: {
    label: 'Information',
    badgeClass: 'bg-blue-100 text-blue-800',
    borderClass: 'border-l-4 border-l-primary',
    icon: faBullhorn,
    bannerBg: 'bg-gradient-to-r from-primary to-accent',
  },
  warning: {
    label: 'Avertissement',
    badgeClass: 'bg-amber-100 text-amber-800',
    borderClass: 'border-l-4 border-l-amber-500',
    icon: faExclamationTriangle,
    bannerBg: 'bg-gradient-to-r from-amber-500 to-amber-600',
  },
  urgent: {
    label: 'Urgent',
    badgeClass: 'bg-red-100 text-red-800',
    borderClass: 'border-l-4 border-l-red-600',
    icon: faExclamationCircle,
    bannerBg: 'bg-gradient-to-r from-red-600 to-red-700',
  },
} as const;

const statusConfig = {
  active: { label: 'Active', className: 'bg-green-100 text-green-800', icon: faCheck },
  inactive: { label: 'Inactive', className: 'bg-gray-100 text-gray-600', icon: faBan },
  expired: { label: 'Expirée', className: 'bg-red-100 text-red-800', icon: faClock },
};

const getStatus = (a: Announcement): 'active' | 'inactive' | 'expired' => {
  if (a.expiresAt && new Date(a.expiresAt) < new Date()) return 'expired';
  return a.active ? 'active' : 'inactive';
};

const Admin: React.FC = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    message: '',
    type: 'info' as Announcement['type'],
    active: true,
    expiresAt: '',
  });
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const {
    announcements,
    activeAnnouncements,
    addAnnouncement,
    updateAnnouncement,
    deleteAnnouncement,
  } = useAnnouncements();

  // ─── Auth state listener ───
  useEffect(() => {
    if (firebaseEnabled && auth) {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        setAuthenticated(!!user);
        setLoading(false);
      });
      return () => unsubscribe();
    } else {
      setAuthenticated(sessionStorage.getItem('cabinet_admin_auth') === 'true');
      setLoading(false);
    }
  }, []);

  const stats = useMemo(() => {
    const now = new Date();
    const expired = announcements.filter(a => a.expiresAt && new Date(a.expiresAt) < now).length;
    const active = activeAnnouncements.length;
    const inactive = announcements.length - active - expired;
    return { total: announcements.length, active, inactive, expired };
  }, [announcements, activeAnnouncements]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    if (firebaseEnabled && auth) {
      try {
        await signInWithEmailAndPassword(auth, email, password);
      } catch (err: any) {
        const code = err?.code || '';
        if (code === 'auth/user-not-found' || code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
          setLoginError('Email ou mot de passe incorrect');
        } else if (code === 'auth/too-many-requests') {
          setLoginError('Trop de tentatives. Réessayez plus tard.');
        } else {
          setLoginError('Erreur de connexion. Vérifiez vos identifiants.');
        }
        setPassword('');
      }
    } else {
      if (password === ADMIN_PASSWORD) {
        setAuthenticated(true);
        sessionStorage.setItem('cabinet_admin_auth', 'true');
      } else {
        setLoginError('Mot de passe incorrect');
        setPassword('');
      }
    }
    setLoginLoading(false);
  };

  const handleLogout = () => {
    if (firebaseEnabled && auth) {
      signOut(auth);
    } else {
      setAuthenticated(false);
      sessionStorage.removeItem('cabinet_admin_auth');
    }
  };

  const resetForm = () => {
    setFormData({ message: '', type: 'info', active: true, expiresAt: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.message.trim()) return;
    if (editingId) {
      updateAnnouncement(editingId, {
        message: formData.message.trim(),
        type: formData.type,
        active: formData.active,
        expiresAt: formData.expiresAt,
      });
    } else {
      addAnnouncement({
        message: formData.message.trim(),
        type: formData.type,
        active: formData.active,
        expiresAt: formData.expiresAt,
      });
    }
    resetForm();
  };

  const startEdit = (a: Announcement) => {
    setFormData({
      message: a.message,
      type: a.type,
      active: a.active,
      expiresAt: a.expiresAt || '',
    });
    setEditingId(a.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (id: string) => {
    deleteAnnouncement(id);
    setDeleteConfirm(null);
  };

  /* ─── LOADING SPINNER ─── */
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-accent via-primary to-blue-light flex items-center justify-center">
        <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  /* ─── LOGIN SCREEN ─── */
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-accent via-primary to-blue-light flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 sm:p-10 w-full max-w-md animate-fade-in">
          <div className="text-center mb-8">
            <img src={logo} alt="Logo" className="h-16 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              Administration
            </h1>
            <p className="text-gray-500 mt-2 text-sm">Gestion des annonces du cabinet</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            {/* Email field — only when Firebase Auth is active */}
            {firebaseEnabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-gray-400" />
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="input-field"
                  placeholder="admin@exemple.com"
                  autoFocus
                  required
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FontAwesomeIcon icon={faLock} className="mr-2 text-gray-400" />
                Mot de passe
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="input-field pr-10"
                  placeholder="Entrez le mot de passe"
                  autoFocus={!firebaseEnabled}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              {loginError && (
                <p className="text-red-500 text-sm mt-2 animate-fade-in">{loginError}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loginLoading}
              className="btn-primary w-full text-center flex items-center justify-center"
            >
              {loginLoading ? (
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
              ) : (
                'Connexion'
              )}
            </button>
          </form>

          <div className="mt-8 text-center">
            <Link to="/" className="text-sm text-gray-400 hover:text-primary transition-colors">
              <FontAwesomeIcon icon={faArrowLeft} className="mr-1" />
              Retour au site
            </Link>
          </div>
        </div>
      </div>
    );
  }

  /* ─── DASHBOARD ─── */
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img src={logo} alt="Logo" className="h-8" />
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Administration</h1>
              <p className="text-xs text-gray-500 hidden sm:block">Gestion des annonces</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <Link
              to="/"
              className="text-sm text-gray-500 hover:text-primary transition-colors hidden sm:flex items-center"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="mr-1.5" />
              Voir le site
            </Link>
            <button
              onClick={handleLogout}
              className="text-sm text-gray-500 hover:text-red-500 transition-colors flex items-center"
            >
              <FontAwesomeIcon icon={faSignOutAlt} className="mr-1.5" />
              <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { value: stats.total, label: 'Total', color: 'text-gray-900', border: 'border-gray-100' },
            { value: stats.active, label: 'Actives', color: 'text-green-600', border: 'border-green-100' },
            { value: stats.inactive, label: 'Inactives', color: 'text-gray-400', border: 'border-gray-100' },
            { value: stats.expired, label: 'Expirées', color: 'text-red-500', border: 'border-red-100' },
          ].map((s, i) => (
            <div key={i} className={`bg-white rounded-xl p-5 shadow-sm border ${s.border}`}>
              <p className={`text-3xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-sm text-gray-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Live preview */}
        {activeAnnouncements.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Aperçu de la bannière
            </h2>
            <div className={`${typeConfig[activeAnnouncements[0].type].bannerBg} text-white rounded-xl overflow-hidden`}>
              <div className="px-6 py-3 flex items-center justify-center space-x-2 text-sm">
                <FontAwesomeIcon icon={typeConfig[activeAnnouncements[0].type].icon} className="w-4 h-4" />
                <span className="font-medium">{activeAnnouncements[0].message}</span>
                {activeAnnouncements.length > 1 && (
                  <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-xs">
                    +{activeAnnouncements.length - 1} autre{activeAnnouncements.length > 2 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action bar */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Annonces</h2>
          <button
            onClick={() => {
              if (showForm && !editingId) {
                resetForm();
              } else {
                resetForm();
                setShowForm(true);
              }
            }}
            className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
              showForm && !editingId
                ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                : 'bg-primary text-white hover:bg-accent'
            }`}
          >
            <FontAwesomeIcon icon={showForm && !editingId ? faTimes : faPlus} className="w-4 h-4" />
            <span>{showForm && !editingId ? 'Annuler' : 'Nouvelle annonce'}</span>
          </button>
        </div>

        {/* ─── ADD / EDIT FORM ─── */}
        {showForm && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8 animate-fade-in">
            <h3 className="text-lg font-semibold text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              {editingId ? "Modifier l'annonce" : 'Nouvelle annonce'}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                <textarea
                  value={formData.message}
                  onChange={e => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  className="input-field resize-none"
                  rows={3}
                  placeholder="Ex: Le cabinet sera fermé le 25 décembre. Joyeuses fêtes !"
                  maxLength={200}
                  required
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{formData.message.length}/200</p>
              </div>

              {/* Type selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Type d'annonce</label>
                <div className="grid grid-cols-3 gap-3">
                  {(Object.keys(typeConfig) as Announcement['type'][]).map(key => {
                    const cfg = typeConfig[key];
                    const selected = formData.type === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, type: key }))}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          selected
                            ? 'border-primary bg-blue-50 shadow-sm'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <FontAwesomeIcon
                          icon={cfg.icon}
                          className={`w-5 h-5 mb-1.5 block mx-auto ${selected ? 'text-primary' : 'text-gray-400'}`}
                        />
                        <p className={`text-xs font-medium ${selected ? 'text-primary' : 'text-gray-600'}`}>
                          {cfg.label}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Inline preview */}
              {formData.message.trim() && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Aperçu</label>
                  <div className={`${typeConfig[formData.type].bannerBg} text-white rounded-lg overflow-hidden`}>
                    <div className="px-4 py-2.5 flex items-center justify-center space-x-2 text-sm">
                      <FontAwesomeIcon icon={typeConfig[formData.type].icon} className="w-4 h-4" />
                      <span className="font-medium">{formData.message}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Active toggle + Expiration */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, active: !prev.active }))}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg border-2 w-full transition-all ${
                      formData.active
                        ? 'border-green-300 bg-green-50'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <FontAwesomeIcon
                      icon={formData.active ? faToggleOn : faToggleOff}
                      className={`w-6 h-6 ${formData.active ? 'text-green-500' : 'text-gray-400'}`}
                    />
                    <span className={`text-sm font-medium ${formData.active ? 'text-green-700' : 'text-gray-500'}`}>
                      {formData.active ? 'Active — visible sur le site' : 'Inactive — masquée'}
                    </span>
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faClock} className="mr-1.5 text-gray-400" />
                    Expiration <span className="text-gray-400 font-normal">(optionnel)</span>
                  </label>
                  <input
                    type="date"
                    value={formData.expiresAt}
                    onChange={e => setFormData(prev => ({ ...prev, expiresAt: e.target.value }))}
                    className="input-field"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-3 pt-2">
                <button type="submit" className="btn-primary">
                  <FontAwesomeIcon icon={faCheck} className="mr-2" />
                  {editingId ? 'Enregistrer' : "Ajouter l'annonce"}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-3 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ─── ANNOUNCEMENTS LIST ─── */}
        {announcements.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <FontAwesomeIcon icon={faBullhorn} className="w-12 h-12 text-gray-200 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune annonce</h3>
            <p className="text-gray-500 mb-6 max-w-sm mx-auto">
              Créez votre première annonce pour informer vos patients de changements d'horaires, fermetures exceptionnelles, etc.
            </p>
            <button onClick={() => setShowForm(true)} className="btn-primary inline-flex items-center">
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Créer une annonce
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {announcements.map(a => {
              const status = getStatus(a);
              const sConfig = statusConfig[status];
              const tConfig = typeConfig[a.type];

              return (
                <div
                  key={a.id}
                  className={`bg-white rounded-xl shadow-sm border border-gray-100 ${tConfig.borderClass} overflow-hidden transition-all hover:shadow-md ${
                    status === 'expired' ? 'opacity-60' : ''
                  }`}
                >
                  <div className="p-5">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Badges */}
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${tConfig.badgeClass}`}>
                            <FontAwesomeIcon icon={tConfig.icon} className="w-3 h-3 mr-1" />
                            {tConfig.label}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${sConfig.className}`}>
                            <FontAwesomeIcon icon={sConfig.icon} className="w-3 h-3 mr-1" />
                            {sConfig.label}
                          </span>
                        </div>
                        {/* Message */}
                        <p className="text-gray-900 font-medium leading-relaxed">{a.message}</p>
                        {/* Meta */}
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2.5 text-xs text-gray-400">
                          <span>Créée le {new Date(a.createdAt).toLocaleDateString('fr-FR')}</span>
                          {a.expiresAt && (
                            <span className={status === 'expired' ? 'text-red-400' : ''}>
                              {status === 'expired' ? 'Expirée le' : 'Expire le'}{' '}
                              {new Date(a.expiresAt).toLocaleDateString('fr-FR')}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center space-x-1 flex-shrink-0">
                        <button
                          onClick={() => updateAnnouncement(a.id, { active: !a.active })}
                          className={`p-2 rounded-lg transition-colors ${
                            a.active
                              ? 'text-green-600 hover:bg-green-50'
                              : 'text-gray-400 hover:bg-gray-100'
                          }`}
                          title={a.active ? 'Désactiver' : 'Activer'}
                        >
                          <FontAwesomeIcon icon={a.active ? faToggleOn : faToggleOff} className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => startEdit(a)}
                          className="p-2 rounded-lg text-primary hover:bg-blue-50 transition-colors"
                          title="Modifier"
                        >
                          <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                        </button>
                        {deleteConfirm === a.id ? (
                          <div className="flex items-center space-x-1 animate-fade-in">
                            <button
                              onClick={() => handleDelete(a.id)}
                              className="px-2.5 py-1 text-xs font-medium text-white bg-red-500 rounded-md hover:bg-red-600 transition-colors"
                            >
                              Supprimer
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                            >
                              Non
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(a.id)}
                            className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                            title="Supprimer"
                          >
                            <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Info box */}
        <div className="mt-10 bg-blue-50 border border-blue-200 rounded-xl p-5 text-sm text-blue-800">
          <p className="font-semibold mb-2">
            <FontAwesomeIcon icon={faBullhorn} className="mr-2" />
            Comment fonctionnent les annonces ?
          </p>
          <ul className="list-disc list-inside space-y-1 text-blue-700">
            <li>Les annonces actives s'affichent en haut de toutes les pages du site</li>
            <li>Les visiteurs peuvent fermer une annonce pour leur session en cours</li>
            <li>Les annonces avec une date d'expiration se masquent automatiquement</li>
            <li>Seul un administrateur authentifié peut créer ou modifier les annonces</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Admin;
